import {Component, HostListener, Injector, OnInit} from '@angular/core';
import {BaseComponent} from '../../base.component';
import {ListResponse} from '../../../network/service/list.response';
import {SnackBarUtil} from '../../../util/snack.bar.util';
import {DataResponse} from '../../../network/service/data.response';
import {FactorStatus, FactorStatusHelper} from '../../../enums/factor.status';
import {RouteUtil} from '../../../util/route.util';
import {RequestType} from '../../../enums/request.type';
import {OrderStatus} from '../../../enums/order.status';

@Component({
  selector: 'app-open-factors',
  standalone: false,
  templateUrl: './open-factors.component.html',
  styleUrl: './open-factors.component.scss'
})
export class OpenFactorsComponent extends BaseComponent implements OnInit {
  branches: any = [];
  orders: any = [];
  filteredOrders: any = [];
  isLoading: boolean = false;
  selectedFactor: any = null;
  showStatusDropdown: boolean = false;
  selectedBranchId?: string;
  remainedTimeToDeliver: number | null = null;
  // Only the statuses that can be changed to
  availableStatuses = [
    {
      value: FactorStatus.PREPARATION,
      label: 'در حال آماده سازی',
      icon: 'restaurant'
    },
    {
      value: FactorStatus.PROCESSING_CENTER,
      label: 'خروج از آشپزخانه',
      icon: 'exit_to_app'
    },
    {
      value: FactorStatus.DELIVERY_DISPATCHER,
      label: 'تحویل به مأمور ارسال',
      icon: 'local_shipping'
    },
    {
      value: FactorStatus.DELIVERED,
      label: 'تحویل به مشتری',
      icon: 'done_all'
    },
    {
      value: FactorStatus.CANCELED,
      label: 'لغو',
      icon: 'cancel'
    }
  ];
  FactorStatuses = [
    FactorStatus.SUBMIT,
    FactorStatus.PENDING_PAYMENT,
    FactorStatus.PROGRESSING,
    FactorStatus.PREPARATION,
    FactorStatus.PROCESSING_CENTER,
    FactorStatus.DELIVERY_DISPATCHER,
    FactorStatus.DELIVERED,
    FactorStatus.CANCELED,
    FactorStatus.CANCELED_BY_SYSTEM
  ].map(status => ({
    value: status,
    label: this.getStatusLabel(status),
    color: this.getStatusColor(status)
  }));

  constructor(injector: Injector) {
    super(injector);
  }

  override ngOnInit() {
    super.ngOnInit();
  }

  override loadOnline() {
    super.loadOnline();
    this.isLoading = true;
    this.serviceApi.getBranches(undefined, undefined, undefined, RequestType.ALL, undefined).subscribe((res: DataResponse<any>) => {
      if (res.success) {
        this.branches = res.data;
        if (this.branches && this.branches.length > 0) {
          this.onBranchClick(this.branches[0]);
        }
      }
    });
  }

  onBranchClick(branch: any) {
    if (this.selectedBranchId !== branch.BusinessId) {
      this.selectedBranchId = branch.BusinessId;
      this.orders = [];
      this.getOpenFactors();
    }
  }

  isBranchSelected(branch: any): boolean {
    return this.selectedBranchId === branch.BusinessId;
  }

  override setListeners() {
    super.setListeners();
  }

  override pageChanged(event: any) {
    super.pageChanged(event);
    this.tableProperties.offset = (event.pageIndex);
    this.loadOnline();
  }

  override onSortChanged(event: any) {
    super.onSortChanged(event);
    this.loadOnline();
  }

  getOpenFactors() {
    this.serviceApi.getFactors(this.tableProperties.offset, this.tableProperties.limit, 'User,ServicePrice,Order,FactorOrders,FactorOrders.Order,FactorOrders.Order.ServicePrice,FactorOrders.Order.ServicePrice.Service,FactorOrders.Order.OrderServicePriceAdditives,FactorOrders.Order.OrderServicePriceAdditives.ServicePriceAdditive,FactorOrders.Order.OrderServicePriceAdditives.ServicePriceAdditive.Attribute,DeliveryMethod,UserAddress,Space', undefined, {
      Filter: {
        Values: [{
          Field: 'OpenFactors',
          Type: 'EQ',
          Value: true
        }]
      },
      Order: this.tableProperties.order ?? {
        Field: 'ModifyDate',
        Type: 'DESC'
      }
    }, this.selectedBranchId).subscribe((res: ListResponse<any>) => {
      this.isLoading = false;
      if (res.success) {
        this.orders = res.data;
        this.orders.map((order: any) => {
          order.FactorOrders.map((factorOrder: any) => {
            if (factorOrder.Order && factorOrder.Order.Status) {
              factorOrder.Order.IsReady = factorOrder.Order.Status === OrderStatus.READY;
            }
            return factorOrder;
          });
          return order;
        });
        this.filteredOrders = [...this.orders];
        this.tableProperties.pageLength = res.count;
        this.setListeners();
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
      }
    });
  }

  refreshData() {
    this.tableProperties.offset = 0;
    this.getOpenFactors();
  }

  getStatusClass(status: string): string {
    // Map status to CSS class based on FactorStatusHelper colors
    const color = FactorStatusHelper.getStatusColor(status);
    if (color === '#2196f3') return 'status-submit';
    if (color === '#ff9800') return 'status-waiting';
    if (color === '#9c27b0') return 'status-waiting';
    if (color === '#607d8b') return 'status-preparation';
    if (color === '#ff5722') return 'status-sent';
    if (color === '#795548') return 'status-sent';
    if (color === '#4caf50') return 'status-delivered';
    if (color === '#f44336') return 'status-canceled';
    return 'status-default';
  }

  getStatusColor(status: string): string {
    return FactorStatusHelper.getStatusColor(status);
  }

  getStatusIcon(status: string): string {
    return FactorStatusHelper.getStatusIcon(status);
  }

  getStatusLabel(status: string): string {
    const statusKey = FactorStatusHelper.getStatusText(status);
    return this.translateService.instant(statusKey);
  }

  viewDetails(factor: any) {
  }

  changeStatus(factor: any) {
    // Don't allow status change for delivered or canceled orders
    if (factor.Status === FactorStatus.DELIVERED ||
      factor.Status === FactorStatus.CANCELED ||
      factor.Status === FactorStatus.CANCELED_BY_SYSTEM) {
      SnackBarUtil.makeMessage(this, 'وضعیت این فاکتور قابل تغییر نیست', false);
      return;
    }
    this.selectedFactor = factor;
    this.remainedTimeToDeliver = null;
    this.showStatusDropdown = !this.showStatusDropdown;
  }

  updateFactorStatus(newStatus: string) {
    if (!this.selectedFactor) return;
    this.isLoading = true;
    const requestBody: any = {Status: newStatus};
    if (this.remainedTimeToDeliver !== null && this.remainedTimeToDeliver !== undefined) {
      requestBody.RemainedTimeToDeliver = this.remainedTimeToDeliver;
    }
    this.serviceApi.updateFactorStatus(this.selectedFactor.FactorId, requestBody)
      .subscribe((res: DataResponse<any>) => {
        this.isLoading = false;
        if (res.success) {
          SnackBarUtil.makeMessage(this, this.translateService.instant('StatusChangeSuccess'), true);
          this.getOpenFactors();
          this.showStatusDropdown = false;
          this.selectedFactor = null;
          this.remainedTimeToDeliver = null;
        } else {
          SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!) || this.translateService.instant('ErrorInStatusChange'), false);
        }
      }, (error) => {
        this.isLoading = false;
        SnackBarUtil.makeMessage(this, this.translateService.instant('ServerConnectionError'), false);
      });
  }

  closeStatusDropdown() {
    this.showStatusDropdown = false;
    this.selectedFactor = null;
    this.remainedTimeToDeliver = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    // Close dropdown if clicking outside
    if (this.showStatusDropdown) {
      const target = event.target as HTMLElement;
      if (!target.closest('.status-change-container') && !target.closest('.status-dropdown')) {
        this.closeStatusDropdown();
      }
    }
  }

  addNewItem(factor: any) {
    // Navigate to place-order page with factor information
    this.router.navigate([RouteUtil.DASHBOARD_MODULE, RouteUtil.PLACE_ORDER_COMPONENT, factor.UserId], {
      queryParams: {
        factorId: factor.FactorId,
        mode: 'add-to-existing'
      }
    }).then();
  }

  toggleProductReady(order: any, isReady: boolean) {
    if (order.Order) {
      this.serviceApi.updateOrderStatus(order.OrderId, isReady ? OrderStatus.READY : OrderStatus.PREPARATION).subscribe((res: DataResponse<any>) => {
        if (res.success) {
          order.Order.IsReady = isReady;
          order.Order.Status = isReady ? OrderStatus.READY : OrderStatus.PREPARATION;
          SnackBarUtil.makeMessage(this, isReady ? this.translateService.instant('ProductReady') : this.translateService.instant('ProductNotReady'), true);
        } else {
          SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
        }
      });
    }
  }

  calculateTotal(factor: any): number {
    let total = 0;
    if (factor.FactorOrders && factor.FactorOrders.length > 0) {
      factor.FactorOrders.forEach((order: any) => {
        if (order.Order?.ServicePrice?.Price && order.Order?.Count) {
          total += order.Order.ServicePrice.Price * order.Order.Count;
        }
      });
    }
    return total;
  }
}
